import classnames from '@app/utils/classnames';
import useConfiguration from '@store/hooks/useConfiguration';
import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckoutFormModel } from '../schema';
import { ICheckoutCustomer, ICheckoutAddress } from '../types';
import ShippingAddressFieldset from './ShippingAddressFieldset';

type IProps =
  | {
    shippingAddresses?: ICheckoutAddress[];
    countries: { [k: string]: string };
  } & (ICheckoutCustomer);

export default function StepCustomer({ shippingAddresses, countries, ...customer }: IProps) {
  const { isAuthenticated } = useConfiguration();

  const { t } = useTranslation();
  const { control, register, formState, setValue } = useFormContext<CheckoutFormModel>();
  const isCompany = useWatch({ control, name: 'customer.isCompany' });

  const showCompanyFieldset = useMemo(
    () => !(isAuthenticated && !isCompany),
    [isCompany, isAuthenticated]
  );

  return (
    <div className="max-w-md w-full flex justify-center">
      <div className="max-h-full w-full grid grid-cols-1 gap-y-4">
        <fieldset className="form-section">
          <legend>{t('PersonalInformation')}</legend>

          <div className="form-fields">
            <div className="form-field">
              <label htmlFor="customer.titlePrefix">{t('TitlePrefix')}</label>
              <div className="input">
                <input
                  id="customer.titlePrefix"
                  type="text"
                  autoComplete="honorific-prefix"
                  className="w-full as-text"
                  {...register('customer.titlePrefix')}
                />
                {formState.errors?.customer?.titlePrefix?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.titlePrefix.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.firstName">{t('FirstName')}</label>
              <div className="input">
                <input
                  id="customer.firstName"
                  type="text"
                  autoComplete="given-name"
                  className="w-full as-text"
                  {...register('customer.firstName')}
                />
                {formState.errors?.customer?.firstName?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.firstName.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.lastName">{t('LastName')}</label>
              <div className="input">
                <input
                  id="customer.lastName"
                  type="text"
                  autoComplete="family-name"
                  className="w-full as-text"
                  {...register('customer.lastName')}
                />
                {formState.errors?.customer?.lastName?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.lastName.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.titleSuffix">{t('TitleSuffix')}</label>
              <div className="input">
                <input
                  id="customer.titleSuffix"
                  type="text"
                  autoComplete="honorific-suffix"
                  className="w-full as-text"
                  {...register('customer.titleSuffix')}
                />
                {formState.errors?.customer?.titleSuffix?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.titleSuffix.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.phoneNumber">{t('PhoneNumber')}</label>
              <div className="input">
                <input
                  id="customer.phoneNumber"
                  type="text"
                  autoComplete="tel"
                  className="w-full as-text"
                  {...register('customer.phoneNumber')}
                />
                {formState.errors?.customer?.phoneNumber?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.phoneNumber.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.emailAddress">{t('EmailAddress')}</label>
              <div className="input">
                <input
                  id="customer.emailAddress"
                  type="text"
                  autoComplete="email"
                  className="w-full as-text"
                  {...register('customer.emailAddress')}
                />
                {formState.errors?.customer?.emailAddress?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer?.emailAddress.message)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </fieldset>
        {showCompanyFieldset && (
          <fieldset className="form-section">
            <legend>{t('CompanyInformation')}</legend>

            {!isAuthenticated && (
              <div className="form-field">
                <div className="input">
                  <label htmlFor="isCompany" className="checkbox">
                    <input
                      type="checkbox"
                      {...register('customer.isCompany')}
                      id="isCompany"
                      onChange={() => {
                        setValue('customer.isCompany', !isCompany);
                        setValue('shippingMethodId', '');
                        setValue('paymentMethodId', '');
                      }}
                    />
                    <span className="text">{t('RegisterAsCompany')}</span>
                  </label>
                </div>
              </div>
            )}

            <div
              className={classnames('form-fields', {
                hidden: !isCompany,
              })}>
              <div className="form-field">
                <label htmlFor="customer.customerName">{t('CompanyName')}</label>
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    id="customer.customerName"
                    {...register('customer.customerName')}
                  />
                  {formState.errors?.customer?.customerName?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.customerName.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="customer.registrationNumber">
                  {t('CompanyRegistrationNumber')}
                </label>
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    id="customer.registrationNumber"
                    {...register('customer.registrationNumber')}
                  />
                  {formState.errors?.customer?.registrationNumber?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.registrationNumber.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="customer.taxNumber">{t('CompanyTaxNumber')}</label>
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    id="customer.taxNumber"
                    {...register('customer.taxNumber')}
                  />
                  {formState.errors?.customer?.taxNumber?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.taxNumber.message)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="customer.vatNumber">{t('CompanyVatNumber')}</label>
                <div className="input">
                  <input
                    type="text"
                    className="w-full as-text"
                    id="customer.vatNumber"
                    {...register('customer.vatNumber')}
                  />
                  {formState.errors?.customer?.vatNumber?.message && (
                    <span className="field-validation-error">
                      {t(formState.errors.customer.vatNumber.message)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </fieldset>
        )}

        <fieldset className="form-section">
          <legend>{t('BillingAddress')}</legend>

          <div className="form-fields">
            <div className="form-field">
              <label htmlFor="customer.billingStreet" className="required">
                {t('Street')}
              </label>
              <div className="input">
                <input
                  id="customer.billingStreet"
                  type="text"
                  autoComplete="street-address"
                  className="w-full as-text"
                  {...register('customer.billingStreet')}
                />
                {formState.errors?.customer?.billingStreet?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingStreet.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.billingTown" className="required">
                {t('Town')}
              </label>
              <div className="input">
                <input
                  id="customer.billingTown"
                  type="text"
                  autoComplete="address-level2"
                  className="w-full as-text"
                  {...register('customer.billingTown')}
                />
                {formState.errors?.customer?.billingTown?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingTown.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.billingPostCode" className="required">
                {t('PostCode')}
              </label>
              <div className="input">
                <input
                  id="customer.billingPostCode"
                  type="text"
                  autoComplete="postal-code"
                  className="w-full as-text"
                  {...register('customer.billingPostCode')}
                />
                {formState.errors?.customer?.billingPostCode?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingPostCode.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="customer.billingCountryCode" className="required">
                {t('Country')}
              </label>
              <div className="input">
                <select
                  id="customer.billingCountryCode"
                  autoComplete="country"
                  className="w-full"
                  {...register('customer.billingCountryCode')}>
                  <option value="">Vyberte</option>
                  {Object.keys(countries).map(code => (
                    <option key={code} value={code}>
                      {countries[code]}
                    </option>
                  ))}
                </select>
                {formState.errors?.customer?.billingCountryCode?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.billingCountryCode.message)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </fieldset>

        <ShippingAddressFieldset addresses={shippingAddresses} countries={countries} />
      </div>
    </div>
  );
}
